import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  Check,
  Loader2,
  Mic,
  RotateCcw,
  Save,
  Sparkles,
  Square
} from 'lucide-react'
import toast from 'react-hot-toast'
import BodyModel from 'react-body-highlighter'

import { useAuth } from '@/context/AuthContext'
import { analyzeVoiceTranscript, type VoiceInsight } from '@/lib/dashboard-ai'
import { fetchHealthEntryByDate, getTodayDate, saveHealthEntry } from '@/lib/health'
import { getActiveMedications } from '@/lib/medications'
import type { ActiveMedication, HealthEntry, HealthEntryInput } from '@/types/health'

type SliderField =
  | 'painLevel'
  | 'fatigueLevel'
  | 'sleepQuality'
  | 'energyLevel'
  | 'moodLevel'
  | 'transitQuality'
  | 'appetiteLevel'
  | 'sleepHours'
  | 'stoolCount'

type BodyView = 'anterior' | 'posterior'

interface PreciseBodyZone {
  key: string
  label: string
  view: BodyView
  left: string
  top: string
  family: 'pelvis' | 'abdomen' | 'thorax' | 'back'
  familyLabel: string
}

interface PainPoint {
  region: string
  view: BodyView
  intensity: number
  painTypes: string[]
}

interface CheckInFormState {
  transcript: string
  painLevel: number | null
  fatigueLevel: number | null
  sleepQuality: number | null
  energyLevel: number | null
  moodLevel: number | null
  transitQuality: number | null
  appetiteLevel: number | null
  sleepHours: number | null
  sleepBedtime: string
  sleepWakeTime: string
  stoolCount: number | null
  bleedingPresent: boolean
  urgentAlert: boolean
  painPoints: PainPoint[]
  painLocations: string[]
  painTypes: string[]
  symptomFlags: string[]
  medicationsTaken: string[]
}

const PAIN_LOCATIONS = [
  'Tete',
  'Machoire',
  'Gorge',
  'Poitrine',
  'Estomac',
  'Flanc gauche',
  'Flanc droit',
  'Bas ventre',
  'Pelvis',
  'Rectum',
  'Dos',
  'Articulations',
  'Jambes'
] as const

const PAIN_TYPES = [
  'Crampes',
  'Brulures',
  'Spasmes',
  'Pression',
  'Elancements',
  'Lourdeur',
  'Piqures',
  'Coups de couteau',
  'Tiraillement',
  'Decharge electrique',
  'Pesanteur pelvienne',
  'Pulsations',
  'Douleur diffuse',
  'Irradiation'
] as const

const BODY_REGION_LABELS: Record<string, string> = {
  head: 'Tête',
  neck: 'Cou',
  chest: 'Poitrine',
  abs: 'Ventre',
  obliques: 'Flancs',
  biceps: 'Biceps',
  triceps: 'Triceps',
  forearm: 'Avant-bras',
  'front-deltoids': 'Épaules avant',
  'back-deltoids': 'Épaules arrière',
  trapezius: 'Trapèzes',
  'upper-back': 'Haut du dos',
  'lower-back': 'Bas du dos',
  quadriceps: 'Cuisses avant',
  hamstring: 'Cuisses arrière',
  adductor: 'Adducteurs',
  abductors: 'Hanches',
  gluteal: 'Fessiers',
  calves: 'Mollets',
  knees: 'Genoux',
  'left-soleus': 'Mollet gauche',
  'right-soleus': 'Mollet droit',
  'breast-left': 'Sein gauche',
  'breast-right': 'Sein droit',
  epigastric: 'Epigastre',
  'abdomen-upper-left': 'Haut ventre gauche',
  'abdomen-upper-right': 'Haut ventre droit',
  'abdomen-lower-left': 'Bas ventre gauche',
  'abdomen-lower-center': 'Bas ventre',
  'abdomen-lower-right': 'Bas ventre droit',
  'flank-left-lower': 'Flanc gauche bas',
  'flank-right-lower': 'Flanc droit bas',
  'pelvis-left': 'Fosse iliaque gauche',
  'pelvis-right': 'Fosse iliaque droite',
  'ovary-left': 'Ovaire gauche',
  'ovary-right': 'Ovaire droit',
  'hip-left': 'Hanche gauche',
  'hip-right': 'Hanche droite',
  bladder: 'Vessie / sus-pubien',
  uterus: 'Uterus / centre pelvien',
  'groin-left': 'Aine gauche',
  'groin-right': 'Aine droite',
  vulva: 'Vulve / vagin',
  perineum: 'Perinee',
  'lumbar-left': 'Lombaire gauche',
  'lumbar-center': 'Lombaires',
  'lumbar-right': 'Lombaire droite',
  'sacro-left': 'Sacro-iliaque gauche',
  'sacro-right': 'Sacro-iliaque droite',
  sacrum: 'Sacrum / bassin',
  'glute-left': 'Fessier gauche',
  'glute-right': 'Fessier droit',
  rectum: 'Zone anale / rectale'
}

const PRECISE_BODY_ZONES: PreciseBodyZone[] = [
  { key: 'breast-left', label: 'Sein gauche', view: 'anterior', left: '41%', top: '28%', family: 'thorax', familyLabel: 'Thorax' },
  { key: 'breast-right', label: 'Sein droit', view: 'anterior', left: '59%', top: '28%', family: 'thorax', familyLabel: 'Thorax' },
  { key: 'epigastric', label: 'Epigastre', view: 'anterior', left: '50%', top: '40%', family: 'abdomen', familyLabel: 'Abdomen' },
  { key: 'abdomen-upper-left', label: 'Haut ventre gauche', view: 'anterior', left: '42%', top: '45%', family: 'abdomen', familyLabel: 'Abdomen' },
  { key: 'abdomen-upper-right', label: 'Haut ventre droit', view: 'anterior', left: '58%', top: '45%', family: 'abdomen', familyLabel: 'Abdomen' },
  { key: 'abdomen-lower-left', label: 'Bas ventre gauche', view: 'anterior', left: '42%', top: '53%', family: 'abdomen', familyLabel: 'Abdomen' },
  { key: 'abdomen-lower-center', label: 'Bas ventre', view: 'anterior', left: '50%', top: '55%', family: 'abdomen', familyLabel: 'Abdomen' },
  { key: 'abdomen-lower-right', label: 'Bas ventre droit', view: 'anterior', left: '58%', top: '53%', family: 'abdomen', familyLabel: 'Abdomen' },
  { key: 'flank-left-lower', label: 'Flanc gauche bas', view: 'anterior', left: '36%', top: '55%', family: 'abdomen', familyLabel: 'Abdomen / pelvis' },
  { key: 'flank-right-lower', label: 'Flanc droit bas', view: 'anterior', left: '64%', top: '55%', family: 'abdomen', familyLabel: 'Abdomen / pelvis' },
  { key: 'pelvis-left', label: 'Fosse iliaque gauche', view: 'anterior', left: '40%', top: '60%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'uterus', label: 'Uterus / centre pelvien', view: 'anterior', left: '50%', top: '62%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'pelvis-right', label: 'Fosse iliaque droite', view: 'anterior', left: '60%', top: '60%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'ovary-left', label: 'Ovaire gauche', view: 'anterior', left: '43%', top: '66%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'bladder', label: 'Vessie / sus-pubien', view: 'anterior', left: '50%', top: '67%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'ovary-right', label: 'Ovaire droit', view: 'anterior', left: '57%', top: '66%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'hip-left', label: 'Hanche gauche', view: 'anterior', left: '37%', top: '67%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'hip-right', label: 'Hanche droite', view: 'anterior', left: '63%', top: '67%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'groin-left', label: 'Aine gauche', view: 'anterior', left: '41%', top: '73%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'vulva', label: 'Vulve / vagin', view: 'anterior', left: '50%', top: '75%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'groin-right', label: 'Aine droite', view: 'anterior', left: '59%', top: '73%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'perineum', label: 'Perinee', view: 'anterior', left: '50%', top: '79%', family: 'pelvis', familyLabel: 'Pelvis / endometriose' },
  { key: 'lumbar-left', label: 'Lombaire gauche', view: 'posterior', left: '42%', top: '48%', family: 'back', familyLabel: 'Dos / pelvis' },
  { key: 'lumbar-center', label: 'Lombaires', view: 'posterior', left: '50%', top: '49%', family: 'back', familyLabel: 'Dos / pelvis' },
  { key: 'lumbar-right', label: 'Lombaire droite', view: 'posterior', left: '58%', top: '48%', family: 'back', familyLabel: 'Dos / pelvis' },
  { key: 'sacro-left', label: 'Sacro-iliaque gauche', view: 'posterior', left: '43%', top: '59%', family: 'pelvis', familyLabel: 'Dos / pelvis' },
  { key: 'sacrum', label: 'Sacrum / bassin', view: 'posterior', left: '50%', top: '60%', family: 'pelvis', familyLabel: 'Dos / pelvis' },
  { key: 'sacro-right', label: 'Sacro-iliaque droite', view: 'posterior', left: '57%', top: '59%', family: 'pelvis', familyLabel: 'Dos / pelvis' },
  { key: 'glute-left', label: 'Fessier gauche', view: 'posterior', left: '44%', top: '67%', family: 'pelvis', familyLabel: 'Dos / pelvis' },
  { key: 'glute-right', label: 'Fessier droit', view: 'posterior', left: '56%', top: '67%', family: 'pelvis', familyLabel: 'Dos / pelvis' },
  { key: 'rectum', label: 'Zone anale / rectale', view: 'posterior', left: '50%', top: '74%', family: 'pelvis', familyLabel: 'Dos / pelvis' }
]

const BODY_HIGHLIGHT_COLORS = [
  '#3b1f1f',
  '#5a2020',
  '#742222',
  '#8c2323',
  '#a22424',
  '#b82626',
  '#cd2d2d',
  '#e13b3b',
  '#ef5252',
  '#f87171'
]

const SYMPTOM_FLAGS = [
  'Nausees',
  'Ballonnements',
  'Diarrhee',
  'Constipation',
  'Saignement',
  'Fievre',
  'Reflux',
  'Maux de tete',
  'Vertiges',
  'Fatigue digestive'
] as const

const SLIDER_CONFIG: Array<{
  key: SliderField
  label: string
  min: number
  max: number
  step?: number
  accentClass: string
}> = [
  { key: 'painLevel', label: 'Douleur', min: 0, max: 10, accentClass: 'text-rose-200' },
  { key: 'fatigueLevel', label: 'Fatigue', min: 0, max: 10, accentClass: 'text-orange-200' },
  { key: 'sleepQuality', label: 'Sommeil', min: 0, max: 10, accentClass: 'text-cyan-200' },
  { key: 'energyLevel', label: 'Energie', min: 0, max: 10, accentClass: 'text-amber-200' },
  { key: 'moodLevel', label: 'Humeur', min: 0, max: 10, accentClass: 'text-emerald-200' },
  { key: 'transitQuality', label: 'Transit', min: 0, max: 10, accentClass: 'text-sky-200' },
  { key: 'appetiteLevel', label: 'Appetit', min: 0, max: 10, accentClass: 'text-violet-200' },
  { key: 'sleepHours', label: 'Heures sommeil', min: 0, max: 24, step: 0.5, accentClass: 'text-slate-100' },
  { key: 'stoolCount', label: 'Selles', min: 0, max: 10, step: 1, accentClass: 'text-slate-100' }
]

function getSafeMetadata(metadata: HealthEntry['metadata']): Record<string, unknown> {
  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata as Record<string, unknown>
  }

  return {}
}

function getMetadataStringArray(metadata: Record<string, unknown>, key: string): string[] {
  const value = metadata[key]

  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
}

function getMetadataString(metadata: Record<string, unknown>, key: string): string {
  const value = metadata[key]
  return typeof value === 'string' ? value.trim() : ''
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])]
}

function toggleValue(currentValues: string[], value: string) {
  return currentValues.includes(value)
    ? currentValues.filter((item) => item !== value)
    : [...currentValues, value]
}

function getPreciseBodyZone(region: string) {
  return PRECISE_BODY_ZONES.find((zone) => zone.key === region) || null
}

function isPreciseBodyZone(region: string) {
  return PRECISE_BODY_ZONES.some((zone) => zone.key === region)
}

function getPainRegionFamilyLabel(region: string) {
  return getPreciseBodyZone(region)?.familyLabel || 'Corps'
}

function getPainRegionLabel(region: string) {
  return getPreciseBodyZone(region)?.label || BODY_REGION_LABELS[region] || region
}

function getPainMarkerColor(intensity: number) {
  const colorIndex = Math.min(
    BODY_HIGHLIGHT_COLORS.length - 1,
    Math.max(0, clampPainIntensity(intensity, 1) - 1)
  )

  return BODY_HIGHLIGHT_COLORS[colorIndex]
}

function clampPainIntensity(value: unknown, fallback = 5) {
  const numeric = typeof value === 'number' ? value : Number.parseFloat(String(value || ''))
  if (!Number.isFinite(numeric)) {
    return fallback
  }

  return Math.min(10, Math.max(0, Math.round(numeric)))
}

function normalizeTimeInput(value: string): string {
  const trimmedValue = value.trim()
  return /^\d{2}:\d{2}$/.test(trimmedValue) ? trimmedValue : ''
}

function convertTimeToMinutes(value: string): number | null {
  const normalizedValue = normalizeTimeInput(value)

  if (!normalizedValue) {
    return null
  }

  const [hours, minutes] = normalizedValue.split(':').map((part) => Number.parseInt(part, 10))

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null
  }

  return hours * 60 + minutes
}

function computeSleepDurationHours(bedtime: string, wakeTime: string): number | null {
  const bedtimeMinutes = convertTimeToMinutes(bedtime)
  const wakeMinutes = convertTimeToMinutes(wakeTime)

  if (bedtimeMinutes === null || wakeMinutes === null) {
    return null
  }

  const adjustedWakeMinutes = wakeMinutes >= bedtimeMinutes ? wakeMinutes : wakeMinutes + 24 * 60
  const durationHours = (adjustedWakeMinutes - bedtimeMinutes) / 60

  if (!Number.isFinite(durationHours) || durationHours < 0 || durationHours > 24) {
    return null
  }

  return Math.round(durationHours * 2) / 2
}

function getMetadataPainPoints(metadata: Record<string, unknown>): PainPoint[] {
  const rawValue = metadata.pain_points

  if (!Array.isArray(rawValue)) {
    return []
  }

  return rawValue
    .map((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return null
      }

      const rawPoint = entry as Record<string, unknown>
      const region =
        typeof rawPoint.region === 'string'
          ? rawPoint.region.trim()
          : typeof rawPoint.region_key === 'string'
            ? rawPoint.region_key.trim()
            : ''
      const view = rawPoint.view === 'posterior' ? 'posterior' : 'anterior'
      const painTypes = Array.isArray(rawPoint.pain_types)
        ? rawPoint.pain_types.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        : Array.isArray(rawPoint.painTypes)
          ? rawPoint.painTypes.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
          : []

      if (!region) {
        return null
      }

      return {
        region,
        view,
        intensity: clampPainIntensity(rawPoint.intensity, 5),
        painTypes
      } satisfies PainPoint
    })
    .filter((point): point is PainPoint => Boolean(point))
}

function uniquePainPoints(points: PainPoint[]) {
  const seen = new Map<string, PainPoint>()

  points.forEach((point) => {
    const key = `${point.view}:${point.region}`
    seen.set(key, {
      ...point,
      intensity: clampPainIntensity(point.intensity, 5),
      painTypes: uniqueStrings(point.painTypes)
    })
  })

  return [...seen.values()]
}

function derivePainLocations(points: PainPoint[]) {
  return uniqueStrings(points.map((point) => getPainRegionLabel(point.region)))
}

function derivePainTypes(points: PainPoint[]) {
  return uniqueStrings(points.flatMap((point) => point.painTypes))
}

function derivePainLevel(points: PainPoint[], fallback: number | null) {
  if (points.length === 0) {
    return fallback
  }

  return points.reduce((max, point) => Math.max(max, clampPainIntensity(point.intensity, 0)), 0)
}

function withPainPoints(form: CheckInFormState, painPoints: PainPoint[]): CheckInFormState {
  const nextPainPoints = uniquePainPoints(painPoints)

  return {
    ...form,
    painPoints: nextPainPoints,
    painLocations: derivePainLocations(nextPainPoints),
    painTypes: derivePainTypes(nextPainPoints),
    painLevel: nextPainPoints.length > 0 ? derivePainLevel(nextPainPoints, form.painLevel) : null
  }
}

function upsertPainPoint(points: PainPoint[], nextPoint: PainPoint) {
  return uniquePainPoints([
    ...points.filter((point) => !(point.region === nextPoint.region && point.view === nextPoint.view)),
    nextPoint
  ])
}

function removePainPoint(points: PainPoint[], target: Pick<PainPoint, 'region' | 'view'>) {
  return points.filter((point) => !(point.region === target.region && point.view === target.view))
}

function buildBodyModelData(points: PainPoint[], view: BodyView) {
  return points
    .filter((point) => point.view === view && !isPreciseBodyZone(point.region))
    .map((point) => ({
      name: `${getPainRegionLabel(point.region)} ${point.intensity}/10`,
      muscles: [point.region as any],
      frequency: Math.max(1, clampPainIntensity(point.intensity, 1))
    }))
}

function summarizePainPoint(point: PainPoint) {
  const types = point.painTypes.length > 0 ? ` · ${point.painTypes.join(', ')}` : ''
  return `${getPainRegionLabel(point.region)} ${point.intensity}/10${types}`
}

function createFormState(entry: HealthEntry | null): CheckInFormState {
  const metadata = getSafeMetadata(entry?.metadata)
  const painPoints = getMetadataPainPoints(metadata)

  return {
    transcript: entry?.voice_note ?? '',
    painLevel: entry?.pain_level ?? null,
    fatigueLevel: entry?.fatigue_level ?? null,
    sleepQuality: entry?.sleep_quality ?? null,
    energyLevel: entry?.energy_level ?? null,
    moodLevel: entry?.mood_level ?? null,
    transitQuality: entry?.transit_quality ?? null,
    appetiteLevel: entry?.appetite_level ?? null,
    sleepHours: entry?.sleep_hours ?? null,
    sleepBedtime: getMetadataString(metadata, 'sleep_bedtime'),
    sleepWakeTime: getMetadataString(metadata, 'sleep_wake_time'),
    stoolCount: entry?.stool_count ?? null,
    bleedingPresent: entry?.bleeding_present ?? false,
    urgentAlert: entry?.urgent_alert ?? false,
    painPoints,
    painLocations:
      painPoints.length > 0
        ? derivePainLocations(painPoints)
        : getMetadataStringArray(metadata, 'pain_locations'),
    painTypes:
      painPoints.length > 0
        ? derivePainTypes(painPoints)
        : getMetadataStringArray(metadata, 'pain_types'),
    symptomFlags:
      getMetadataStringArray(metadata, 'symptom_flags').length > 0
        ? getMetadataStringArray(metadata, 'symptom_flags')
        : entry?.symptoms ?? [],
    medicationsTaken: entry?.medications_taken ?? []
  }
}

function buildAutoSummary(form: CheckInFormState, insight: VoiceInsight | null) {
  const parts: string[] = []

  if (insight?.summary?.trim()) {
    parts.push(insight.summary.trim())
  }

  if (form.painPoints.length > 0) {
    parts.push(`Douleurs: ${form.painPoints.map((point) => summarizePainPoint(point)).join(' | ')}`)
  } else if (form.painLocations.length > 0) {
    const painType = form.painTypes.length > 0 ? `${form.painTypes.join(', ')} ` : ''
    parts.push(`${painType}sur ${form.painLocations.join(', ')}`)
  }

  if (form.symptomFlags.length > 0) {
    parts.push(`Signaux: ${form.symptomFlags.join(', ')}`)
  }

  return parts.length > 0 ? parts.join(' | ') : null
}

function applyVoiceInsight(current: CheckInFormState, insight: VoiceInsight): CheckInFormState {
  return {
    ...current,
    painLevel: current.painLevel ?? insight.pain_level ?? null,
    sleepQuality: current.sleepQuality ?? insight.sleep_quality ?? null,
    energyLevel: current.energyLevel ?? insight.energy_level ?? null,
    moodLevel: current.moodLevel ?? insight.mood_level ?? null,
    urgentAlert: current.urgentAlert || insight.urgent_alert,
    symptomFlags: uniqueStrings([...current.symptomFlags, ...insight.symptoms]),
    medicationsTaken: uniqueStrings([...current.medicationsTaken, ...insight.medications_taken])
  }
}

function MetricSlider({
  label,
  value,
  min,
  max,
  step = 1,
  accentClass,
  onChange
}: {
  label: string
  value: number | null
  min: number
  max: number
  step?: number
  accentClass: string
  onChange: (nextValue: number) => void
}) {
  return (
    <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-sm text-slate-300">{label}</span>
        <span className={`text-sm font-semibold ${accentClass}`}>
          {typeof value === 'number' ? value : '--'}
        </span>
      </div>

      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={typeof value === 'number' ? value : min}
        onChange={(event) => onChange(Number(event.target.value))}
        className="w-full accent-primary-500"
      />
    </div>
  )
}

function ToggleChip({
  label,
  active,
  onClick
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-2 text-sm transition-colors ${
        active
          ? 'border-primary-500/40 bg-primary-500/15 text-white'
          : 'border-dark-border bg-dark-surface-high text-slate-300 hover:text-white'
      }`}
    >
      {label}
    </button>
  )
}

export default function DailyCheckIn() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const recognitionRef = useRef<any>(null)
  const today = getTodayDate()
  const [selectedDate, setSelectedDate] = useState(today)
  const [form, setForm] = useState<CheckInFormState>(() => createFormState(null))
  const [bodyView, setBodyView] = useState<BodyView>('anterior')
  const [editingPainPoint, setEditingPainPoint] = useState<PainPoint | null>(null)
  const [customPainTypeInput, setCustomPainTypeInput] = useState('')
  const [isListening, setIsListening] = useState(false)
  const [voiceInsight, setVoiceInsight] = useState<VoiceInsight | null>(null)
  const [analyzedTranscript, setAnalyzedTranscript] = useState('')

  const entryQuery = useQuery({
    queryKey: ['health-entry', user?.id, selectedDate, 'daily-checkin-v2'],
    queryFn: () => fetchHealthEntryByDate(user!.id, selectedDate),
    enabled: Boolean(user)
  })

  const medicationsQuery = useQuery({
    queryKey: ['medications', user?.id, 'daily-checkin-v2'],
    queryFn: () => getActiveMedications(user!.id),
    enabled: Boolean(user)
  })

  useEffect(() => {
    setForm(createFormState(entryQuery.data ?? null))
    setEditingPainPoint(null)
    setCustomPainTypeInput('')
    setVoiceInsight(null)
    setAnalyzedTranscript('')
  }, [entryQuery.data, selectedDate])

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop?.()
    }
  }, [])

  const activeMedications = medicationsQuery.data ?? []
  const medicationChoices = useMemo(
    () =>
      uniqueStrings([
        ...activeMedications.map((medication: ActiveMedication) => medication.name),
        ...form.medicationsTaken,
        ...(voiceInsight?.medications_taken ?? [])
      ]),
    [activeMedications, form.medicationsTaken, voiceInsight]
  )

  const symptomChoices = useMemo(
    () => uniqueStrings([...SYMPTOM_FLAGS, ...form.symptomFlags, ...(voiceInsight?.symptoms ?? [])]),
    [form.symptomFlags, voiceInsight]
  )

  const SpeechRecognitionApi =
    typeof window !== 'undefined'
      ? (
          window as Window & {
            SpeechRecognition?: any
            webkitSpeechRecognition?: any
          }
        ).SpeechRecognition ||
        (
          window as Window & {
            SpeechRecognition?: any
            webkitSpeechRecognition?: any
          }
        ).webkitSpeechRecognition
      : null

  const analyzeMutation = useMutation({
    mutationFn: async (transcript: string) => analyzeVoiceTranscript(user!.id, transcript),
    onSuccess: (insight, transcript) => {
      setVoiceInsight(insight)
      setAnalyzedTranscript(transcript)
      setForm((current) => applyVoiceInsight(current, insight))
      toast.success('Vocal analyse')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Analyse impossible')
    }
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      let insight = voiceInsight
      const transcript = form.transcript.trim()

      if (transcript && (!insight || analyzedTranscript !== transcript)) {
        insight = await analyzeVoiceTranscript(user!.id, transcript)
        setVoiceInsight(insight)
        setAnalyzedTranscript(transcript)
        setForm((current) => applyVoiceInsight(current, insight!))
      }

      const baseEntry = entryQuery.data ?? null
      const metadata = getSafeMetadata(baseEntry?.metadata)
      const mergedSymptoms = uniqueStrings([...form.symptomFlags, ...(insight?.symptoms ?? [])])
      const mergedMedications = uniqueStrings([
        ...form.medicationsTaken,
        ...(insight?.medications_taken ?? [])
      ])
      const normalizedPainPoints = uniquePainPoints(
        editingPainPoint
          ? upsertPainPoint(form.painPoints, editingPainPoint)
          : form.painPoints
      )
      const painLocations = normalizedPainPoints.length > 0 ? derivePainLocations(normalizedPainPoints) : form.painLocations
      const painTypes = normalizedPainPoints.length > 0 ? derivePainTypes(normalizedPainPoints) : form.painTypes
      const painLevel = derivePainLevel(normalizedPainPoints, form.painLevel)
      const painZoneFamilies = uniqueStrings(
        normalizedPainPoints.map((point) => getPainRegionFamilyLabel(point.region))
      )
      const computedSleepHours = computeSleepDurationHours(form.sleepBedtime, form.sleepWakeTime)

      const payload: HealthEntryInput = {
        owner_id: user!.id,
        entry_date: selectedDate,
        summary: buildAutoSummary(form, insight),
        voice_note: transcript || null,
        pain_level: painLevel,
        fatigue_level: form.fatigueLevel,
        sleep_quality: form.sleepQuality,
        energy_level: form.energyLevel,
        mood_level: form.moodLevel,
        transit_quality: form.transitQuality,
        appetite_level: form.appetiteLevel,
        sleep_hours: computedSleepHours ?? form.sleepHours,
        stool_count: form.stoolCount,
        symptoms: mergedSymptoms,
        medications_taken: mergedMedications,
        bleeding_present: form.bleedingPresent,
        urgent_alert: form.urgentAlert || insight?.urgent_alert || false,
        metadata: {
          ...metadata,
          journal_mode: 'quick_checkin_v2',
          pain_locations: painLocations,
          pain_types: painTypes,
          pain_zone_families: painZoneFamilies,
          pain_points_version: 2,
          pain_points: normalizedPainPoints.map((point) => ({
            region: point.region,
            region_key: point.region,
            region_label: getPainRegionLabel(point.region),
            region_family: getPainRegionFamilyLabel(point.region),
            view: point.view,
            intensity: point.intensity,
            pain_types: point.painTypes
          })),
          symptom_flags: form.symptomFlags,
          sleep_bedtime: form.sleepBedtime || null,
          sleep_wake_time: form.sleepWakeTime || null,
          sleep_duration_hours: computedSleepHours ?? form.sleepHours ?? null,
          last_voice_analysis: insight
            ? {
                summary: insight.summary,
                watch_items: insight.watch_items
              }
            : metadata.last_voice_analysis ?? null,
          last_voice_analysis_at: insight
            ? new Date().toISOString()
            : metadata.last_voice_analysis_at ?? null
        }
      }

      return saveHealthEntry(payload)
    },
    onSuccess: async () => {
      toast.success('Journal enregistre')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['health-entry', user?.id, selectedDate] }),
        queryClient.invalidateQueries({ queryKey: ['health-entries', user?.id] })
      ])
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Enregistrement impossible')
    }
  })

  const handleVoiceButtonClick = () => {
    if (isListening) {
      recognitionRef.current?.stop?.()
      return
    }

    if (!SpeechRecognitionApi) {
      toast.error('Reconnaissance vocale non disponible sur cet appareil')
      return
    }

    let finalTranscript = ''
    const recognition = new SpeechRecognitionApi()
    recognition.lang = 'fr-FR'
    recognition.continuous = true
    recognition.interimResults = true

    recognition.onresult = (event: any) => {
      let interimTranscript = ''

      for (let index = event.resultIndex; index < event.results.length; index++) {
        const transcript = event.results[index][0]?.transcript ?? ''

        if (event.results[index].isFinal) {
          finalTranscript = `${finalTranscript} ${transcript}`.trim()
        } else {
          interimTranscript = `${interimTranscript} ${transcript}`.trim()
        }
      }

      setForm((current) => ({
        ...current,
        transcript: `${finalTranscript} ${interimTranscript}`.trim()
      }))
    }

    recognition.onend = () => {
      setIsListening(false)
      recognitionRef.current = null
      const transcript = finalTranscript.trim() || form.transcript.trim()

      if (transcript) {
        setForm((current) => ({
          ...current,
          transcript
        }))
        analyzeMutation.mutate(transcript)
      }
    }

    recognition.onerror = () => {
      setIsListening(false)
      recognitionRef.current = null
      toast.error('Impossible de lancer le vocal')
    }

    recognitionRef.current = recognition
    setIsListening(true)
    recognition.start()
  }

  const bodyModelData = useMemo(
    () => buildBodyModelData(form.painPoints, bodyView),
    [bodyView, form.painPoints]
  )

  const preciseZonesForCurrentView = useMemo(
    () => PRECISE_BODY_ZONES.filter((zone) => zone.view === bodyView),
    [bodyView]
  )

  const openPainPointEditor = (region: string, view: BodyView) => {
    const existingPoint = form.painPoints.find((point) => point.region === region && point.view === view)

    setCustomPainTypeInput('')
    setEditingPainPoint(
      existingPoint || {
        region,
        view,
        intensity: clampPainIntensity(form.painLevel, 5),
        painTypes: []
      }
    )
  }

  const handleBodyRegionClick = ({ muscle }: { muscle?: string }) => {
    const region = typeof muscle === 'string' ? muscle : ''

    if (!region) {
      return
    }

    openPainPointEditor(region, bodyView)
  }

  const handleSavePainPoint = () => {
    if (!editingPainPoint) {
      return
    }

    setForm((current) => withPainPoints(current, upsertPainPoint(current.painPoints, editingPainPoint)))
    setCustomPainTypeInput('')
    setEditingPainPoint(null)
  }

  const handleDeletePainPoint = () => {
    if (!editingPainPoint) {
      return
    }

    setForm((current) => withPainPoints(current, removePainPoint(current.painPoints, editingPainPoint)))
    setCustomPainTypeInput('')
    setEditingPainPoint(null)
  }

  const handleAddCustomPainType = () => {
    const normalizedPainType = customPainTypeInput.trim()

    if (!normalizedPainType || !editingPainPoint) {
      return
    }

    setEditingPainPoint((current) =>
      current
        ? {
            ...current,
            painTypes: uniqueStrings([...current.painTypes, normalizedPainType])
          }
        : current
    )
    setCustomPainTypeInput('')
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-cyan-400" />
            <h1 className="text-2xl font-bold text-white">Journal quotidien</h1>
          </div>
          <p className="text-sm text-slate-400">
            {format(new Date(`${selectedDate}T12:00:00`), 'EEEE d MMMM yyyy', { locale: fr })}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            className="input max-w-[190px]"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            className="btn-primary"
            disabled={saveMutation.isPending}
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <section className="card space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-primary-300" />
            <h2 className="text-lg font-semibold text-white">Vocal</h2>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleVoiceButtonClick}
              className={isListening ? 'btn-secondary border-red-500/30 text-red-100' : 'btn-primary'}
            >
              {isListening ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              {isListening ? 'Arreter' : 'Parler'}
            </button>

            {form.transcript.trim() ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setForm((current) => ({ ...current, transcript: '' }))
                    setVoiceInsight(null)
                    setAnalyzedTranscript('')
                  }}
                  className="btn-ghost"
                >
                  <RotateCcw className="h-4 w-4" />
                  Effacer
                </button>

                {analyzedTranscript !== form.transcript.trim() && (
                  <button
                    type="button"
                    onClick={() => analyzeMutation.mutate(form.transcript.trim())}
                    className="btn-secondary"
                    disabled={analyzeMutation.isPending}
                  >
                    {analyzeMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    Analyser
                  </button>
                )}
              </>
            ) : null}
          </div>
        </div>

        <div className="rounded-2xl border border-dark-border bg-[#07111f] p-4">
          {form.transcript.trim() ? (
            <p className="text-sm leading-6 text-slate-200">{form.transcript}</p>
          ) : (
            <p className="text-sm text-slate-500">Dicter le ressenti du jour, puis ajuster les curseurs si besoin.</p>
          )}
        </div>

        {voiceInsight && (
          <div className="grid gap-3 xl:grid-cols-[1.4fr_0.6fr]">
            <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Lecture rapide</p>
              <p className="mt-2 text-sm leading-6 text-slate-200">{voiceInsight.summary}</p>
            </div>

            <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-500">A surveiller</p>
              <div className="mt-2 space-y-1.5">
                {voiceInsight.watch_items.length > 0 ? (
                  voiceInsight.watch_items.map((item) => (
                    <p key={item} className="text-sm text-slate-300">
                      {item}
                    </p>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">Aucun signal particulier.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="card space-y-4">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-rose-300" />
          <h2 className="text-lg font-semibold text-white">Douleur</h2>
        </div>

        <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-2xl border border-dark-border bg-[#07111f] p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-white">Carte du corps</p>
                <p className="text-xs text-slate-500">Cliquer sur une zone pour renseigner type et intensité.</p>
              </div>

              <div className="inline-flex rounded-full border border-dark-border bg-dark-surface-high p-1">
                <button
                  type="button"
                  onClick={() => {
                    setBodyView('anterior')
                    setEditingPainPoint((current) => current?.view === 'anterior' ? current : null)
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                    bodyView === 'anterior' ? 'bg-primary-500 text-white' : 'text-slate-300'
                  }`}
                >
                  Avant
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setBodyView('posterior')
                    setEditingPainPoint((current) => current?.view === 'posterior' ? current : null)
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                    bodyView === 'posterior' ? 'bg-primary-500 text-white' : 'text-slate-300'
                  }`}
                >
                  Arrière
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="relative mx-auto w-full max-w-[17rem]">
                  <BodyModel
                    data={bodyModelData as any}
                    onClick={handleBodyRegionClick as any}
                    type={bodyView as any}
                    bodyColor="#182332"
                    highlightedColors={BODY_HIGHLIGHT_COLORS}
                    style={{ width: '100%', maxWidth: '17rem', margin: '0 auto', cursor: 'pointer' }}
                    svgStyle={{ width: '100%', height: 'auto' }}
                  />

                  {preciseZonesForCurrentView.map((zone) => {
                    const savedPoint = form.painPoints.find(
                      (point) => point.region === zone.key && point.view === zone.view
                    )
                    const isEditing =
                      editingPainPoint?.region === zone.key && editingPainPoint.view === zone.view

                    return (
                      <button
                        key={zone.key}
                        type="button"
                        title={zone.label}
                        onClick={() => openPainPointEditor(zone.key, zone.view)}
                        className={`absolute z-10 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 rounded-full border transition-all ${
                          savedPoint
                            ? 'border-white/30 text-white shadow-lg'
                            : 'border-rose-200/40 bg-slate-950/80 text-transparent hover:border-rose-300/70'
                        } ${isEditing ? 'ring-2 ring-rose-300/70 ring-offset-2 ring-offset-[#07111f]' : ''}`}
                        style={{
                          left: zone.left,
                          top: zone.top,
                          backgroundColor: savedPoint ? getPainMarkerColor(savedPoint.intensity) : undefined
                        }}
                      >
                        {savedPoint ? (
                          <span className="text-[9px] font-semibold leading-none">
                            {savedPoint.intensity}
                          </span>
                        ) : (
                          <span className="sr-only">{zone.label}</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Zones fines</p>
                <div className="flex flex-wrap gap-2">
                  {preciseZonesForCurrentView.map((zone) => {
                    const isSelected =
                      editingPainPoint?.region === zone.key && editingPainPoint.view === zone.view
                    const hasSavedPoint = form.painPoints.some(
                      (point) => point.region === zone.key && point.view === zone.view
                    )

                    return (
                      <button
                        key={`${zone.view}:${zone.key}:pill`}
                        type="button"
                        onClick={() => openPainPointEditor(zone.key, zone.view)}
                        className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                          isSelected
                            ? 'border-rose-300/60 bg-rose-500/20 text-white'
                            : hasSavedPoint
                              ? 'border-rose-500/30 bg-rose-500/10 text-rose-100'
                              : 'border-dark-border bg-dark-surface-high text-slate-300 hover:text-white'
                        }`}
                      >
                        {zone.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Zones</p>
                <p className="mt-2 text-2xl font-semibold text-white">{form.painPoints.length}</p>
              </div>

              <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Douleur max</p>
                <p className="mt-2 text-2xl font-semibold text-rose-200">{form.painLevel ?? 0}/10</p>
              </div>
            </div>

            {editingPainPoint ? (
              <div className="space-y-4 rounded-2xl border border-rose-500/20 bg-rose-950/10 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">{getPainRegionLabel(editingPainPoint.region)}</p>
                    <p className="mt-1 text-xs text-rose-200/80">
                      {getPainRegionFamilyLabel(editingPainPoint.region)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {editingPainPoint.view === 'anterior' ? 'Vue avant' : 'Vue arrière'}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setCustomPainTypeInput('')
                      setEditingPainPoint(null)
                    }}
                    className="btn-ghost px-3 py-1.5 text-xs"
                  >
                    Fermer
                  </button>
                </div>

                <MetricSlider
                  label="Intensité locale"
                  value={editingPainPoint.intensity}
                  min={0}
                  max={10}
                  accentClass="text-rose-200"
                  onChange={(nextValue) =>
                    setEditingPainPoint((current) => current ? { ...current, intensity: nextValue } : current)
                  }
                />

                <div className="space-y-3">
                  <p className="text-sm text-slate-300">Type de douleur</p>
                  <div className="flex flex-wrap gap-2">
                    {PAIN_TYPES.map((item) => (
                      <ToggleChip
                        key={item}
                        label={item}
                        active={editingPainPoint.painTypes.includes(item)}
                        onClick={() =>
                          setEditingPainPoint((current) =>
                            current
                              ? {
                                  ...current,
                                  painTypes: toggleValue(current.painTypes, item)
                                }
                              : current
                          )
                        }
                      />
                    ))}
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={customPainTypeInput}
                      onChange={(event) => setCustomPainTypeInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          handleAddCustomPainType()
                        }
                      }}
                      className="input flex-1"
                      placeholder="Ajouter un type libre"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomPainType}
                      className="btn-secondary"
                    >
                      Ajouter
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap justify-between gap-2">
                  <button
                    type="button"
                    onClick={handleDeletePainPoint}
                    className="btn-ghost text-xs text-rose-200"
                  >
                    Retirer cette zone
                  </button>

                  <button
                    type="button"
                    onClick={handleSavePainPoint}
                    className="btn-primary"
                  >
                    Enregistrer la zone
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                <p className="text-sm text-slate-300">
                  Choisir une zone sur le corps pour ajouter une douleur, puis sélectionner son type et son intensité.
                </p>
              </div>
            )}

            {form.painPoints.length > 0 && (
              <div className="space-y-3 rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                <p className="text-sm font-medium text-white">Zones enregistrées</p>
                <div className="flex flex-wrap gap-2">
                  {form.painPoints.map((point) => (
                    <button
                      key={`${point.view}:${point.region}`}
                      type="button"
                      onClick={() => {
                        setBodyView(point.view)
                        openPainPointEditor(point.region, point.view)
                      }}
                      className="rounded-full border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-left text-xs text-rose-100 transition-colors hover:border-rose-400/40"
                    >
                      {summarizePainPoint(point)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="card space-y-4">
        <h2 className="text-lg font-semibold text-white">Etat du jour</h2>
        <div className="grid gap-3 md:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
          <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm text-slate-300">Heures de sommeil</p>
              <span className="text-sm font-semibold text-slate-100">
                {typeof form.sleepHours === 'number' ? `${form.sleepHours}h` : '--'}
              </span>
            </div>

            <input
              type="range"
              min={0}
              max={24}
              step={0.5}
              value={typeof form.sleepHours === 'number' ? form.sleepHours : 0}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  sleepHours: Number(event.target.value)
                }))
              }
              className="w-full accent-primary-500"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
              <label className="mb-2 block text-sm text-slate-300">Heure coucher</label>
              <input
                type="time"
                value={form.sleepBedtime}
                onChange={(event) =>
                  setForm((current) => {
                    const nextBedtime = event.target.value
                    const nextSleepHours = computeSleepDurationHours(nextBedtime, current.sleepWakeTime)

                    return {
                      ...current,
                      sleepBedtime: nextBedtime,
                      sleepHours: nextSleepHours ?? current.sleepHours
                    }
                  })
                }
                className="input"
              />
            </div>

            <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
              <label className="mb-2 block text-sm text-slate-300">Heure reveil</label>
              <input
                type="time"
                value={form.sleepWakeTime}
                onChange={(event) =>
                  setForm((current) => {
                    const nextWakeTime = event.target.value
                    const nextSleepHours = computeSleepDurationHours(current.sleepBedtime, nextWakeTime)

                    return {
                      ...current,
                      sleepWakeTime: nextWakeTime,
                      sleepHours: nextSleepHours ?? current.sleepHours
                    }
                  })
                }
                className="input"
              />
            </div>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {SLIDER_CONFIG.filter((metric) => metric.key !== 'painLevel' && metric.key !== 'sleepHours').map((metric) => (
            <MetricSlider
              key={metric.key}
              label={metric.label}
              value={form[metric.key]}
              min={metric.min}
              max={metric.max}
              step={metric.step}
              accentClass={metric.accentClass}
              onChange={(nextValue) =>
                setForm((current) => ({
                  ...current,
                  [metric.key]: nextValue
                }))
              }
            />
          ))}
        </div>
      </section>

      <section className="card space-y-4">
        <h2 className="text-lg font-semibold text-white">Signaux</h2>

        <div className="space-y-3">
          <p className="text-sm text-slate-300">Symptomes</p>
          <div className="flex flex-wrap gap-2">
            {symptomChoices.map((item) => (
              <ToggleChip
                key={item}
                label={item}
                active={form.symptomFlags.includes(item)}
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    symptomFlags: toggleValue(current.symptomFlags, item)
                  }))
                }
              />
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-sm text-slate-300">Traitements pris</p>
          <div className="flex flex-wrap gap-2">
            {medicationChoices.length > 0 ? (
              medicationChoices.map((item) => (
                <ToggleChip
                  key={item}
                  label={item}
                  active={form.medicationsTaken.includes(item)}
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      medicationsTaken: toggleValue(current.medicationsTaken, item)
                    }))
                  }
                />
              ))
            ) : (
              <div className="rounded-2xl border border-dark-border bg-dark-surface-high px-3 py-2 text-sm text-slate-500">
                Aucun traitement actif
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              setForm((current) => ({
                ...current,
                bleedingPresent: !current.bleedingPresent
              }))
            }
            className={`rounded-full border px-3 py-2 text-sm transition-colors ${
              form.bleedingPresent
                ? 'border-red-500/40 bg-red-500/15 text-red-100'
                : 'border-dark-border bg-dark-surface-high text-slate-300'
            }`}
          >
            {form.bleedingPresent ? <Check className="mr-2 inline h-4 w-4" /> : null}
            Saignement
          </button>

          <button
            type="button"
            onClick={() =>
              setForm((current) => ({
                ...current,
                urgentAlert: !current.urgentAlert
              }))
            }
            className={`rounded-full border px-3 py-2 text-sm transition-colors ${
              form.urgentAlert
                ? 'border-amber-500/40 bg-amber-500/15 text-amber-100'
                : 'border-dark-border bg-dark-surface-high text-slate-300'
            }`}
          >
            {form.urgentAlert ? <Check className="mr-2 inline h-4 w-4" /> : null}
            Prioritaire
          </button>
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          className="btn-primary min-w-56"
          disabled={saveMutation.isPending}
        >
          <Save className="h-4 w-4" />
          {saveMutation.isPending ? 'Enregistrement...' : 'Enregistrer le journal'}
        </button>
      </div>
    </div>
  )
}
